import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { buildDiscordLoginUrl, fetchAuthMe, logoutAccount } from '../services/authApi';
import { trackEvent } from '../services/analytics';
import { NOTIFICATION, useNotification } from '../components/NotificationSystem';

/** How often a *visible* tab re-checks the session. A hidden tab does not poll
 *  at all: returning to it fires visibilitychange, which refreshes immediately,
 *  so a background timer only ever asks questions nobody is waiting on. It used
 *  to run unconditionally to keep the Fly machine warm — min_machines_running=1
 *  covers that now, and an unconditional heartbeat is what stops any
 *  usage-billed host (or database) from ever idling. */
const AUTH_POLL_MS = 60000;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState('loading');
    const [user, setUser] = useState(null);
    const { showNotification } = useNotification();
    const didResolveOnceRef = useRef(false);
    const previousAuthStateRef = useRef('loading');
    const pendingAuthResultRef = useRef(null);

    const refreshAuth = useCallback(async () => {
        setAuthState(prev => (prev === 'authenticated' ? prev : 'loading'));
        try {
            const payload = await fetchAuthMe();
            if (payload?.authenticated && payload?.user) {
                setUser(payload.user);
                setAuthState('authenticated');
            } else {
                setUser(null);
                setAuthState('anonymous');
            }
        } catch (error) {
            console.warn('refreshAuth failed:', error);
            // A failed request is not a signed-out session. `/auth/me` answers
            // 200 with `authenticated: false` when nobody is logged in, so a
            // throw here means the network or the API is down — and dropping
            // the user on that showed the Connect button to people whose
            // session was perfectly good. Keep what we know; the next poll,
            // focus or visibility change re-asks.
            setAuthState(prev => (prev === 'authenticated' ? prev : 'anonymous'));
        }
    }, []);

    useEffect(() => {
        refreshAuth();
    }, [refreshAuth]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const authResult = params.get('auth');
        if (!authResult) return;

        pendingAuthResultRef.current = authResult;
        params.delete('auth');
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
    }, []);

    useEffect(() => {
        let poll = null;
        const startPolling = () => {
            if (poll === null) poll = setInterval(refreshAuth, AUTH_POLL_MS);
        };
        const stopPolling = () => {
            if (poll !== null) {
                clearInterval(poll);
                poll = null;
            }
        };

        const onFocus = () => {
            refreshAuth();
        };
        const onVisibility = () => {
            if (document.hidden) {
                stopPolling();
                return;
            }
            // Catch up on whatever changed while the tab was hidden, then resume.
            refreshAuth();
            startPolling();
        };

        if (!document.hidden) startPolling();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopPolling();
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [refreshAuth]);

    useEffect(() => {
        if (authState === 'loading') {
            return;
        }
        const pendingAuthResult = pendingAuthResultRef.current;
        if (pendingAuthResult === 'discord-connected') {
            if (authState === 'authenticated') {
                showNotification('Connected to Discord.', NOTIFICATION.SUCCESS);
            } else {
                showNotification('Discord connection finished, but the session was not found.', NOTIFICATION.ERROR);
            }
            pendingAuthResultRef.current = null;
            didResolveOnceRef.current = true;
            previousAuthStateRef.current = authState;
            return;
        }
        if (pendingAuthResult === 'discord-failed') {
            showNotification('Discord connection failed. Please try again.', NOTIFICATION.ERROR);
            pendingAuthResultRef.current = null;
            didResolveOnceRef.current = true;
            previousAuthStateRef.current = authState;
            return;
        }
        if (!didResolveOnceRef.current) {
            didResolveOnceRef.current = true;
            previousAuthStateRef.current = authState;
            return;
        }
        const prev = previousAuthStateRef.current;
        if (prev === 'anonymous' && authState === 'authenticated') {
            showNotification('Connected to Discord.', NOTIFICATION.SUCCESS);
        }
        previousAuthStateRef.current = authState;
    }, [authState, showNotification]);

    const startLogin = useCallback((nextPath, src) => {
        trackEvent('discord_login', src ? { src } : undefined);
        window.location.assign(buildDiscordLoginUrl(nextPath, src));
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutAccount();
        } catch (error) {
            console.warn('logout failed:', error);
        } finally {
            setUser(null);
            setAuthState('anonymous');
        }
    }, []);

    const value = useMemo(() => ({
        authState,
        user,
        isAuthenticated: authState === 'authenticated',
        isAdmin: !!user?.is_admin,
        refreshAuth,
        startLogin,
        logout,
    }), [authState, user, refreshAuth, startLogin, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export default AuthContext;
